import { useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import colors from "@styling/colors";
import { borderRadius, spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";

const SHOW_DELAY_MS = 120;
const VIEWPORT_MARGIN = spacing.x1;
const BUBBLE_MAX_WIDTH = 280;
// Below this distance from the top of the window the bubble opens downward.
const MIN_SPACE_ABOVE = 48;

const Anchor = styled.span<{ $block?: boolean }>`
  display: ${({ $block }) => ($block ? "block" : "inline-flex")};
  min-width: 0;
`;

const Bubble = styled.div<{ $below: boolean }>`
  position: fixed;
  z-index: 1200;
  max-width: ${BUBBLE_MAX_WIDTH}px;
  padding: ${spacing.x05}px ${spacing.x1}px;
  background-color: ${colors.text};
  color: ${colors.background};
  font-size: ${fontSizes.small}px;
  line-height: 1.4;
  border-radius: ${borderRadius.small}px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  pointer-events: none;
  transform: ${({ $below }) =>
    $below
      ? `translate(-50%, ${spacing.x05}px)`
      : `translate(-50%, calc(-100% - ${spacing.x05}px))`};
`;

interface TooltipProps {
  content: ReactNode;
  children: ReactNode;
  // Stretch the anchor to its container, for tooltips on table cells and inputs.
  block?: boolean;
}

// Shows quickly on hover, unlike the native `title`, and renders in a portal so
// scrolling containers such as tables do not clip it. Hidden while the anchor
// has focus so it does not cover an input being edited.
export const Tooltip = ({ content, children, block }: TooltipProps) => {
  const anchorRef = useRef<HTMLSpanElement>(null);
  const timerRef = useRef<number>(undefined);
  const [position, setPosition] = useState<{
    top: number;
    left: number;
    below: boolean;
  }>();

  const hide = () => {
    window.clearTimeout(timerRef.current);
    setPosition(undefined);
  };

  useEffect(() => () => window.clearTimeout(timerRef.current), []);

  useEffect(() => {
    if (!position) {
      return;
    }
    window.addEventListener("scroll", hide, true);
    return () => window.removeEventListener("scroll", hide, true);
  }, [position]);

  return (
    <Anchor
      ref={anchorRef}
      $block={block}
      onMouseEnter={() => {
        window.clearTimeout(timerRef.current);
        if (!content) {
          return;
        }
        timerRef.current = window.setTimeout(() => {
          const anchor = anchorRef.current;
          if (!anchor || anchor.contains(document.activeElement)) {
            return;
          }
          const rect = anchor.getBoundingClientRect();
          const below = rect.top < MIN_SPACE_ABOVE;
          setPosition({
            top: below ? rect.bottom : rect.top,
            left: Math.min(
              Math.max(
                rect.left + rect.width / 2,
                BUBBLE_MAX_WIDTH / 2 + VIEWPORT_MARGIN,
              ),
              window.innerWidth - BUBBLE_MAX_WIDTH / 2 - VIEWPORT_MARGIN,
            ),
            below,
          });
        }, SHOW_DELAY_MS);
      }}
      onMouseLeave={hide}
      onFocus={hide}
      onMouseDown={hide}
    >
      {children}
      {content &&
        position &&
        createPortal(
          <Bubble
            role="tooltip"
            $below={position.below}
            style={{ top: position.top, left: position.left }}
          >
            {content}
          </Bubble>,
          document.body,
        )}
    </Anchor>
  );
};
